import firebase from "firebase/app";
import "firebase/firestore";

/**
 * NOTE: Connects To Firebase
 * fetch the global private-release invitation settings (expiry + visibility)
 */
function fetchInvitationSettings(db){
  return db.collection("settings").doc("private_release").get()
    .then(doc => {
      if(!doc.exists) return { isShown: false, expiresAt: null }
      let data = doc.data() || {}
      return {
        isShown: !!data.isShown,
        expiresAt: data.expiresAt ? data.expiresAt.toMillis() : null,
      }
    })
    .catch((error) => {
      console.error("Error getting private_release settings:", error);
      return { isShown: false, expiresAt: null }
    });
}

/**
 * NOTE: Connects To Firebase
 * subscribe to realtime updates of the invitation settings doc
 * @returns {function} unsubscribe function
 */
function subscribeInvitationSettings(db, onUpdate, onError){
  return db.collection("settings").doc("private_release").onSnapshot(
    (snapshot) => {
      if(!snapshot.exists){
        onUpdate({ isShown: false, expiresAt: null })
        return
      }
      let data = snapshot.data() || {}
      onUpdate({
        isShown: !!data.isShown,
        expiresAt: data.expiresAt ? data.expiresAt.toMillis() : null,
      })
    },
    (error) => {
      console.error("Error listening to private_release settings:", error);
      if(onError) onError(error)
    }
  );
}

/**
 * NOTE: Connects To Firebase
 * fetch the current user's response (accepted/declined) to the invitation, if any
 */
function fetchUserInvitationResponse(db, uid){
  return db.collection("private_release_invitations").doc(uid).get()
    .then(doc => doc.exists ? doc.data() : null)
    .catch((error) => {
      console.error("Error getting private_release_invitations document:", error);
      return null
    });
}

/**
 * NOTE: Connects To Firebase
 * record that the user opened the invitation modal, but only if there's no
 * existing record yet (never downgrades an accepted/declined doc back to 'viewed')
 */
function recordInvitationView(db, { uid, email }){
  return fetchUserInvitationResponse(db, uid)
    .then(existing => {
      if(existing) return // already viewed/accepted/declined — don't touch it
      return db.collection("private_release_invitations").doc(uid).set({
        uid,
        email: email || null,
        status: "viewed",
        viewedAt: firebase.firestore.FieldValue.serverTimestamp(),
        userAgent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
      })
    })
    .catch((error) => {
      console.error("Error recording invitation view:", error);
    });
}

/**
 * NOTE: Connects To Firebase
 * permanently record the user's accept/decline response to the invitation.
 * Merge-write so it never touches the doc's existing (immutable) viewedAt.
 */
function recordInvitationResponse(db, { uid, email, status }){
  return db.collection("private_release_invitations").doc(uid).set({
    uid,
    email: email || null,
    status,
    respondedAt: firebase.firestore.FieldValue.serverTimestamp(),
    userAgent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
  }, { merge: true })
    .catch((error) => {
      console.error("Error recording invitation response:", error);
      throw error
    });
}

/**
 * Derive HH:MM:SS countdown parts from a fixed expiry timestamp (ms).
 * Purely based on epoch millis, so it's unaffected by the device's timezone setting.
 */
function getCountdownParts(expiresAtMs){
  if(!expiresAtMs) return { hours: 0, minutes: 0, seconds: 0, expired: true }
  let remainingMs = expiresAtMs - Date.now()
  if(remainingMs <= 0) return { hours: 0, minutes: 0, seconds: 0, expired: true }
  let totalSeconds = Math.floor(remainingMs / 1000)
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    expired: false,
  }
}

export const invitationService = {
  fetchInvitationSettings, subscribeInvitationSettings, fetchUserInvitationResponse, recordInvitationView, recordInvitationResponse, getCountdownParts
}
